/**
 * The model + effort controls that live INSIDE the composer's input surface
 * (round 3, owner: "move the toggles to input text field" — the way
 * ChatGPT, Claude and Conductor put the model picker on the bottom edge of
 * the box you type in).
 *
 * Round 7 replaced the pair of native selects with the two controls
 * Conductor's composer footer carries: a mark and the model's spoken name
 * (ModelPicker, which opens a grouped popover upward), then the effort meter
 * and a five-bar meter with the level beside it (EffortMeter). Owner: "I want
 * Conductor style for model picker. Right now the dropdown picker looks
 * ugly" and "Same as effort level, make it bar style like Conductor."
 *
 * One component, both composers: the product home stages the pair locally
 * and sends them in the session create body, while a session writes them
 * straight to its own row. The session header therefore carries no second
 * copy of the same control.
 *
 * Neither control shows the word "Default" in the footer: it shows the model
 * and the level that will actually run, which is the true answer to "what am
 * I about to ask". Whether that came from a choice or from the account
 * default is the quiet difference between an accent meter and a muted one,
 * and the ticked row in the popover.
 */

import type { Effort } from '../../api/types';
import { EffortMeter } from './EffortMeter';
import { ModelPicker } from './ModelPicker';

export function ComposerPickers({
  model,
  effort,
  defaultModel,
  defaultEffort,
  onModel,
  onEffort,
  disabled = false,
  disabledReason,
  modelTestId,
  effortTestId,
}: {
  /** the chosen override, or null for "inherit the default" */
  model: string | null;
  effort: Effort | null;
  /** what "Default" resolves to right now */
  defaultModel: string;
  defaultEffort: Effort;
  onModel: (model: string | null) => void;
  onEffort: (effort: Effort | null) => void;
  disabled?: boolean;
  /** what to say instead of the usual tooltip while disabled */
  disabledReason?: string;
  modelTestId: string;
  effortTestId: string;
}) {
  return (
    <>
      <ModelPicker
        testId={modelTestId}
        model={model}
        defaultModel={defaultModel}
        onChange={onModel}
        onTier={(tierModel, tierEffort) => {
          if (tierModel !== model) onModel(tierModel);
          if (tierEffort !== effort) onEffort(tierEffort);
        }}
        disabled={disabled}
        disabledReason={disabledReason}
      />
      <EffortMeter
        testId={effortTestId}
        effort={effort}
        defaultEffort={defaultEffort}
        onChange={onEffort}
        disabled={disabled}
        disabledReason={disabledReason}
      />
    </>
  );
}
